import React, { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Loader2, RefreshCw, CheckCircle2, XCircle, AlertTriangle, Clock,
  Wifi, WifiOff, CreditCard, Mail, Video, MessageSquare, Calendar,
  BarChart3, Activity, Shield
} from "lucide-react";

const IntegrationsPage = () => {
  const { token } = useAuth();
  const [health, setHealth] = useState(null);
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [hRes, sRes, eRes] = await Promise.all([
        fetch(`${API}/staff/integrations/health`, { headers }),
        fetch(`${API}/staff/integrations/stats`, { headers }),
        fetch(`${API}/staff/integrations/events?limit=20`, { headers }),
      ]);
      if (hRes.ok) setHealth(await hRes.json());
      if (sRes.ok) setStats(await sRes.json());
      if (eRes.ok) { const d = await eRes.json(); setEvents(d.events || []); }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const runCheck = async () => {
    setChecking(true);
    try {
      const res = await fetch(`${API}/staff/integrations/health/check`, { method: "POST", headers });
      if (res.ok) { const d = await res.json(); toast.success(d.message); fetchAll(); }
      else toast.error("Health check failed");
    } catch { toast.error("Connection error"); }
    finally { setChecking(false); }
  };

  const statusIcon = (s) => {
    switch (s) {
      case "healthy": return <CheckCircle2 className="w-5 h-5 text-green-600" />;
      case "not_configured": return <WifiOff className="w-5 h-5 text-stone-400" />;
      case "not_connected": return <AlertTriangle className="w-5 h-5 text-amber-500" />;
      case "auth_error": return <XCircle className="w-5 h-5 text-red-500" />;
      case "degraded": return <AlertTriangle className="w-5 h-5 text-amber-500" />;
      case "timeout": return <Clock className="w-5 h-5 text-amber-500" />;
      default: return <XCircle className="w-5 h-5 text-red-500" />;
    }
  };

  const statusBadge = (s) => {
    const map = {
      healthy: "bg-green-50 text-green-700 border-green-200",
      not_configured: "bg-stone-100 text-stone-500 border-stone-200",
      not_connected: "bg-amber-50 text-amber-700 border-amber-200",
      auth_error: "bg-red-50 text-red-700 border-red-200",
      degraded: "bg-amber-50 text-amber-700 border-amber-200",
      timeout: "bg-amber-50 text-amber-700 border-amber-200",
      error: "bg-red-50 text-red-700 border-red-200",
    };
    return <Badge className={`${map[s] || map.error} text-xs`}>{s?.replace("_", " ")}</Badge>;
  };

  const integrationMeta = {
    paystack: { label: "Paystack Payments", icon: CreditCard, desc: "Payment processing and webhook verification" },
    zoho_smtp: { label: "Zoho SMTP (Email)", icon: Mail, desc: "Transactional email delivery" },
    zoho_bookings: { label: "Zoho Bookings", icon: Calendar, desc: "Appointment booking and calendar sync" },
    google_calendar: { label: "Google Calendar + Meet", icon: Video, desc: "Calendar events and video meeting links" },
    whatsapp: { label: "WhatsApp", icon: MessageSquare, desc: "WhatsApp click-to-chat for patient communications" },
  };

  if (loading) return <div className="flex items-center justify-center h-40"><Loader2 className="w-6 h-6 animate-spin text-stone-400" /></div>;

  return (
    <div className="space-y-6" data-testid="integrations-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-stone-900 tracking-tight">Integration Health</h1>
          <p className="mt-1 text-base text-stone-500">Monitor the status of all external service connections.</p>
        </div>
        <Button onClick={runCheck} disabled={checking} variant="outline" className="rounded-lg" data-testid="run-check-btn">
          {checking ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />} Run health check
        </Button>
      </div>

      {/* Overall Status */}
      {health && (
        <Card className={`rounded-xl p-5 ${
          health.overall === "all_healthy" ? "border-green-200 bg-green-50/30" :
          health.overall === "has_errors" ? "border-red-200 bg-red-50/30" :
          "border-amber-200 bg-amber-50/30"
        }`} data-testid="overall-status">
          <div className="flex items-center gap-3">
            {health.overall === "all_healthy" ? <Wifi className="w-6 h-6 text-green-600" /> : <Activity className="w-6 h-6 text-amber-600" />}
            <div>
              <p className="text-base font-bold text-stone-900">
                {health.healthy}/{health.total} integrations healthy
              </p>
              <p className="text-xs text-stone-500">
                {health.configured}/{health.total} configured. Last checked: {health.checked_at?.split("T")[1]?.split(".")[0]} UTC
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Integration Cards */}
      {health && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(health.integrations).map(([key, result]) => {
            const meta = integrationMeta[key] || { label: key, icon: Shield, desc: "" };
            const Icon = meta.icon;
            const s = stats?.[key] || {};
            return (
              <Card key={key} className="rounded-xl border-stone-200/60 p-5 space-y-3" data-testid={`integration-${key}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${result.status === "healthy" ? "bg-green-100" : result.status === "not_configured" ? "bg-stone-100" : "bg-amber-100"}`}>
                      <Icon className={`w-5 h-5 ${result.status === "healthy" ? "text-green-600" : result.status === "not_configured" ? "text-stone-400" : "text-amber-600"}`} />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-stone-900">{meta.label}</p>
                      <p className="text-xs text-stone-400">{meta.desc}</p>
                    </div>
                  </div>
                  {statusIcon(result.status)}
                </div>

                <div className="flex items-center gap-2">
                  {statusBadge(result.status)}
                  {result.response_ms && <span className="text-xs text-stone-400">{Math.round(result.response_ms)}ms</span>}
                </div>

                <p className="text-xs text-stone-500">{result.message}</p>

                {/* Quick stats */}
                {key === "paystack" && s.total_transactions > 0 && (
                  <div className="text-xs text-stone-400 pt-1 border-t border-stone-100">
                    {s.successful} successful, {s.failed} failed ({s.last_7_days} last 7 days)
                  </div>
                )}
                {key === "zoho_smtp" && s.total_emails > 0 && (
                  <div className="text-xs text-stone-400 pt-1 border-t border-stone-100">
                    {s.sent} sent, {s.failed} failed ({s.last_7_days} last 7 days)
                  </div>
                )}
                {key === "google_calendar" && (s.calendar_events_created > 0 || s.meet_links_generated > 0) && (
                  <div className="text-xs text-stone-400 pt-1 border-t border-stone-100">
                    {s.calendar_events_created} events, {s.meet_links_generated} Meet links
                  </div>
                )}
                {key === "whatsapp" && s.total_messages > 0 && (
                  <div className="text-xs text-stone-400 pt-1 border-t border-stone-100">
                    {s.sent} sent, {s.delivered} delivered, {s.failed} failed
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* Recent Integration Events */}
      <div>
        <h3 className="text-sm font-bold text-stone-500 uppercase tracking-wider mb-3">Recent integration events</h3>
        {events.length > 0 ? (
          <Card className="rounded-xl border-stone-200/60 overflow-hidden">
            <div className="divide-y divide-stone-100">
              {events.map(ev => (
                <div key={ev.event_id} className="px-5 py-3 flex items-center gap-3">
                  <Badge variant="outline" className="text-xs capitalize shrink-0">{ev.provider}</Badge>
                  <span className="text-sm text-stone-700 flex-1">{ev.event_type?.replace(/_/g, " ")}</span>
                  <Badge className={`text-xs ${ev.status === "failed" ? "bg-red-50 text-red-700" : ev.status === "processed" ? "bg-green-50 text-green-700" : "bg-stone-100 text-stone-600"}`}>{ev.status}</Badge>
                  <span className="text-xs text-stone-400 shrink-0">{ev.created_at?.split("T")[0]}</span>
                </div>
              ))}
            </div>
          </Card>
        ) : (
          <Card className="rounded-xl border-stone-200/60 p-8 text-center">
            <Activity className="w-8 h-8 text-stone-300 mx-auto mb-2" />
            <p className="text-sm text-stone-500">No integration events logged yet.</p>
          </Card>
        )}
      </div>
    </div>
  );
};

export default IntegrationsPage;
